import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

import { ApiCaller } from '@graphit/callers'
import type { NodeIdsWithPos } from '@graphit/types'

import { produce } from 'immer'

import { cartographiesQueryOptions } from '~/queries/cartographies'

export const cartographyQueryOptions = (id: string) =>
	queryOptions({
		queryKey: ['cartography', id],
		queryFn: () => ApiCaller.getCartography(id)
	})

export const useCartographyQuery = (id: string) => useSuspenseQuery(cartographyQueryOptions(id))

export const publicCartographyQueryOptions = (id: string) =>
	queryOptions({
		queryKey: ['public-cartography', id],
		queryFn: () => ApiCaller.getPublicCartography(id)
	})

export const usePublicCartographyQuery = (id: string) => useSuspenseQuery(publicCartographyQueryOptions(id))

export const useCartographyReportQuery = (id: string, enabled: boolean) =>
	useQuery({
		queryKey: ['cartography', id, 'report'],
		queryFn: () => ApiCaller.getReportData(id),
		enabled
	})

export const useImportNodesMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			cartographyId,
			nodeIds,
			labelId,
			typeId
		}: {
			cartographyId: string
			nodeIds?: string[]
			labelId?: string
			typeId?: string
		}) => ApiCaller.importNodes(cartographyId, { nodeIds, labelId, typeId }),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['cartography', variables.cartographyId] })
		}
	})
}

export const useImportWorkspaceIntoCartographyMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ cartographyId, workspaceId }: { cartographyId: string; workspaceId: string }) =>
			ApiCaller.importWorkspace(cartographyId, workspaceId),
		onSuccess: (data, variables) => {
			queryClient.setQueryData(cartographyQueryOptions(variables.cartographyId).queryKey, data)
		}
	})
}

export const useRemoveCartographyNodesMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ cartographyId, nodeIds }: { cartographyId: string; nodeIds: string[] }) =>
			ApiCaller.removeNodes(cartographyId, nodeIds),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['cartography', variables.cartographyId] })
		}
	})
}

export const useUpdateNodePositionMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ cartographyId, node }: { cartographyId: string; node: NodeIdsWithPos }) =>
			ApiCaller.updateNodePosition(cartographyId, node),
		onSuccess: (_, variables) => {
			queryClient.setQueryData(cartographyQueryOptions(variables.cartographyId).queryKey, cartography => {
				if (!cartography) return cartography

				return produce(cartography, draft => {
					const node = draft.nodes.find(node => node.id === variables.node.nodeId)

					if (!node) return

					node.x = variables.node.pos?.x
					node.y = variables.node.pos?.y
				})
			})
		}
	})
}

export const useSaveCartographyZoomMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, zoom }: { id: string; zoom: number }) => ApiCaller.updateCartography(id, { zoom }),
		onSuccess: (_, variables) => {
			queryClient.setQueryData(cartographyQueryOptions(variables.id).queryKey, old => {
				if (!old) return old

				return {
					...old,
					zoom: variables.zoom
				}
			})
		}
	})
}

export const useUnlockCartographyPositionMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ cartographyId }: { cartographyId: string }) => ApiCaller.unlockCartographyPosition(cartographyId),
		onSuccess: (_, variables) => {
			queryClient.setQueryData(cartographyQueryOptions(variables.cartographyId).queryKey, cartography => {
				if (!cartography) return cartography

				return produce(cartography, draft => {
					for (const node of draft.nodes) {
						node.x = undefined
						node.y = undefined
					}
				})
			})
		}
	})
}

export const useToggleCartographySharableMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, isPublic }: { id: string; isPublic: boolean }) =>
			ApiCaller.toggleCartographySharable(id, isPublic),
		onSuccess: (data, variables) => {
			queryClient.setQueryData(cartographyQueryOptions(variables.id).queryKey, old => {
				if (!old) return old
				return { ...old, isPublic: data.isPublic }
			})
			queryClient.setQueryData(cartographiesQueryOptions.queryKey, old => {
				if (!old) return old
				return old.map(c => (c.id === variables.id ? { ...c, isPublic: data.isPublic } : c))
			})
		}
	})
}
